import { useState, useRef, useContext, useEffect, useMemo, useCallback } from 'react';
import { format } from "date-fns";
import { de } from "date-fns/locale";

import classes from './MessageSender.module.css';
import { NavBarContext } from '../App';
import ProfileMenu from './NavBar/ProfileMenu';
import DateChooser from './Booking/BookingPhone/DateChooser';
import { SmallModal } from './UI/SmallModal/SmallModal';

import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useBooking } from '../store/bookingStore';
import { parseLocalDateString } from '../util/parseLocalDate';
import { hasUnavailableBusyIntervals } from '../util/studioAvailability';
import {
    getInquiryEndTimeSlots,
    getInquiryStartTimeSlots,
} from '../util/inquiryAvailability';
import { trackHostInquirySent } from '../util/tracking';
import { useChatRooms } from '../hooks/messages/useChatRooms';
import useCreateChatRoom from '../hooks/messages/useCreateChatRoom';
import useCreateMessage from '../hooks/messages/useCreateMessage';
import noUserImage from "../public/fakeUser/noUser.webp";
import { notifyBlockedToast, notifyErrorToast } from '../util/notifyActionError';

const MessageSender = 
    ({ 
        studioPricePerHour,
        userId,
        studioUserId, 
        studioId, 
        studioName, 
        studioImage, 
        onClose, 
        studioUserProfilePicture,
        rating,
        city,
        studioUserFirstName,
        bookingStudioData, 
        minHours
    }) => {

    const { user } = useSelector((state) => state?.auth);
    const { profilePicture } = user;
    const { setActiveView, setShowSignIn } = useContext(NavBarContext);
    const userContainerRef = useRef(null);
    const profileMenuRef = useRef(null);
    const navigate = useNavigate();

    const { bookingData } = useBooking();
    
    const currentUserId = localStorage?.getItem('userId');
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const [showChatSelectionModal, setShowChatSelectionModal] = useState(false);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const availabilityUnavailable = hasUnavailableBusyIntervals(bookingStudioData);
    
    // Get all chat rooms for the current user
    const { chatRooms } = useChatRooms(currentUserId);
    const { createNewChatRoom, loading: creatingChatRoom } = useCreateChatRoom();
    const { sendMessage } = useCreateMessage();
    
    const [selectedStartTime, setSelectedStartTime] = useState('');
    const [selectedEndTime, setSelectedEndTime] = useState('');
    const [times, setTimes] = useState([]);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const datePickerRef = useRef(null);

    const [messageText, setMessageText] = useState('');
    const [isFlexible, setIsFlexible] = useState(false);
    
    const [errors, setErrors] = useState({
        dateTime: '',
        message: ''
    });
        

    const handleProfileMenuClick = () => {
        setShowProfileMenu(prev => !prev);
    };

    // Define calculateDuration function before useMemo
    const calculateDuration = (start, end) => {
        if (!start || !end) return 0;
        const startDate = new Date(`2000-01-01T${start}`);
        const endDate = new Date(`2000-01-01T${end === "00:00" ? "24:00" : end}`);
        
        if (end === "24:00") {
          endDate.setDate(endDate.getDate() + 1);
        }
        const diffMs = endDate - startDate;
        const diffHours = diffMs / (1000 * 60 * 60);
        
        return diffHours;
    };

    // Memoize the validation function
    const isFormValid = useMemo(() => {
        // If date/time section is not checked, it's valid
        if (!isChecked) {
            const isMessageValid = messageText.trim().length > 0;
            return isMessageValid;
        }
        
        if (availabilityUnavailable) {
            return false;
        }

        // If date/time section is checked, we need date
        const hasDate = bookingData.date;
        
        // If flexible time is selected, we don't need specific times
        if (isFlexible) {
            const isMessageValid = messageText.trim().length > 0;
            return hasDate && isMessageValid;
        }
        
        // If not flexible, we need specific start and end times
        const hasTimes = selectedStartTime && selectedEndTime;
        const isMessageValid = messageText.trim().length > 0;
        return hasDate && hasTimes && isMessageValid;
    }, [
        availabilityUnavailable,
        isChecked,
        bookingData.date,
        selectedStartTime,
        selectedEndTime,
        messageText,
        isFlexible,
    ]);

    const validateForm = useCallback(() => {
        const newErrors = {
            dateTime: '',
            message: ''
        };

        // Check date/time validation only if the section is checked
        if (isChecked) {
            if (availabilityUnavailable) {
                newErrors.dateTime = 'Verfügbarkeit konnte gerade nicht geladen werden. Bitte versuche es später erneut.';
            } else if (!bookingData.date) {
                newErrors.dateTime = 'Bitte wähle ein Datum aus.';
            } else if (!isFlexible && (!selectedStartTime || !selectedEndTime)) {
                newErrors.dateTime = 'Bitte wähle Start- und Endzeit aus.';
            }
        }

        // Check message validation
        if (!messageText.trim()) {
            newErrors.message = 'Gib bitte eine Nachricht ein.';
        }

        setErrors(newErrors);
        return !newErrors.dateTime && !newErrors.message;
    }, [
        availabilityUnavailable,
        isChecked,
        bookingData.date,
        selectedStartTime,
        selectedEndTime,
        messageText,
        isFlexible,
    ]);

    const handleFlexibleChange = (e) => {
        const flexible = e?.target?.checked;
        setIsFlexible(flexible);
        
        // Clear date/time error when flexible status changes
        if (errors.dateTime) {
            setErrors(prev => ({ ...prev, dateTime: '' }));
        }
        
        // Don't reset times when flexible is checked - keep them for message options
    };

    const handleMessageChange = (e) => {
        setMessageText(e.target.value);
        if (errors.message) {
            setErrors(prev => ({ ...prev, message: '' }));
        }
    };

    // Memoize the buildMessageContent function
    const messageContent = useMemo(() => {
        let content = messageText;
        
        // Add date and time information if provided
        if (isChecked && bookingData.date) {
            content += '\n\n📅 Gewünschtes Datum: ' + format(parseLocalDateString(bookingData.date), 'dd.MM.yyyy', { locale: de });
            
            // If both specific times and flexible are selected, offer both options
            if (isFlexible && selectedStartTime && selectedEndTime) {
                const duration = calculateDuration(selectedStartTime, selectedEndTime);
                content += `\n⏰ Bevorzugte Zeit: ${selectedStartTime} - ${selectedEndTime} (${duration} Stunden)`;
                content += '\n⏰ Alternativ bin ich auch zeitlich flexibel und kann den ganzen Tag';
            } else if (!isFlexible && selectedStartTime && selectedEndTime) {
                const duration = calculateDuration(selectedStartTime, selectedEndTime);
                content += `\n⏰ Zeit: ${selectedStartTime} - ${selectedEndTime} (${duration} Stunden)`;
            } else if (isFlexible) {
                content += '\n⏰ Ich bin zeitlich flexibel und kann den ganzen Tag';
            }
        }
        
        return content;
    }, [messageText, isChecked, bookingData.date, isFlexible, selectedStartTime, selectedEndTime]);

    // Check if a chat's booking has expired/ended (bookingEndDate is in the past)
    const isChatBookingExpired = useCallback((chat) => {
        const hasLinkedBookingIDs = Array.isArray(chat?.linkedBookingIDs) && chat.linkedBookingIDs.length > 0;
        const isBookingChat = Boolean(
            chat?.isBookingRelated ||
            chat?.chatType === 'BOOKING' ||
            chat?.threadKind === 'BOOKING' ||
            chat?.linkedBookingID ||
            hasLinkedBookingIDs ||
            chat?.bookingEndDate
        );

        // Only check if it's a booking-related chat
        if (!isBookingChat) {
            return false; // Not a booking-related chat, so not expired
        }
        
        // If there's a bookingEndDate, check if it's in the past
        if (chat?.bookingEndDate) {
            const bookingEndDate = new Date(chat.bookingEndDate);
            const now = new Date();
            const isExpired = now > bookingEndDate;
            return isExpired; // Booking has ended
        }
        
        return false; // No end date, assume not expired
    }, []);

    // Get all existing chats for this studio (excluding expired booking chats)
    const existingChatsForStudio = useMemo(() => {
        if (!chatRooms || !studioId) {
            return [];
        }
        const filtered = chatRooms.filter(room => {
            // Filter by studio ID
            if (room.studioID !== studioId) return false;
            // Exclude expired booking chats
            if (isChatBookingExpired(room)) {
                return false;
            }
            return true;
        });
        return filtered;
    }, [chatRooms, studioId, isChatBookingExpired]);

    const handleSendMessage = async (e) => {
        
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        // Check if user is not authenticated
        if (!currentUserId || currentUserId === '' || currentUserId === null) {
            setShowSignIn(true);
            return;
        }

        // Check if user is trying to message their own studio
        if (currentUserId === studioUserId) {
            notifyBlockedToast('Du kannst keine Nachricht an dein eigenes Studio senden.');
            return;
        }

        // Check if all existing chats have expired bookings
        const allChatsExpired = existingChatsForStudio.length > 0 && 
            existingChatsForStudio.every(chat => isChatBookingExpired(chat));
        
        if (allChatsExpired) {
            notifyBlockedToast('Die Buchung für diesen Chat ist abgelaufen. Du kannst keine neue Nachricht senden.');
            return;
        }

        setIsSendingMessage(true);

        try {
            // Case 1: No chat exists - create new chat and send inquiry message
            if (existingChatsForStudio.length === 0) {
                // Create new chat room
                const chatRoomId = await createNewChatRoom(
                    studioId,
                    studioName,
                    studioImage,
                    currentUserId,
                    userId
                );

                // Send the inquiry message
                await sendMessage({
                    content: messageContent,
                    chatRoomId: chatRoomId,
                    senderID: currentUserId
                });
                trackHostInquirySent({
                    studioId,
                    source: 'studio_message',
                    hasRequestedDate: Boolean(isChecked && bookingData.date),
                    isFlexible
                });

                // Navigate to the new chat room
                navigate(`/messages/${chatRoomId}`, {
                    state: { fromProfileMenu: false },
                    replace: true
                });
            }
            // Case 2: One chat exists - redirect to it
            else if (existingChatsForStudio.length === 1) {
                const chatRoomId = existingChatsForStudio[0].id;
                navigate(`/messages/${chatRoomId}`, {
                    state: { fromProfileMenu: false },
                    replace: true
                });
            }
            // Case 3: Multiple chats exist - show modal to select
            else {
                setIsSendingMessage(false); // Reset loading state since we're just showing modal
                setShowChatSelectionModal(true);
            }
        } catch (error) {
            console.error('[ERROR] Error sending message:', error);
            console.error('[ERROR] Error details:', {
                message: error?.message,
                graphQLErrors: error?.graphQLErrors,
                networkError: error?.networkError,
                stack: error?.stack
            });
            const errorMessage = error?.message || 'Fehler beim Senden der Nachricht. Bitte versuche es erneut.';
            notifyErrorToast(errorMessage, {
              title: 'Nachricht konnte nicht gesendet werden',
              onRetry: () => handleSendMessage({ preventDefault: () => {} }),
            });
            setIsSendingMessage(false);
        }
    };

    const handleChatRoomSelect = async (chatRoomId) => {
        setShowChatSelectionModal(false);
        navigate(`/messages/${chatRoomId}`, {
            state: { fromProfileMenu: false },
            replace: true
        });
    };

    const generateTimeSlots = useCallback(() => getInquiryStartTimeSlots({
        dateString: bookingData.date,
        bookingStudioData,
        minHours,
    }), [bookingData.date, bookingStudioData, minHours]);

    const generateEndTimeSlots = useCallback((startTime) => getInquiryEndTimeSlots({
        dateString: bookingData.date,
        startTime,
        bookingStudioData,
        minHours,
    }), [bookingData.date, bookingStudioData, minHours]);

    const handleStartTimeChange = (e) => {
        const newStartTime = e.target.value;
        setSelectedStartTime(newStartTime);
        
        // Only reset end time if the current selection doesn't meet minimum hours
        if (selectedEndTime && newStartTime) {
            const duration = calculateDuration(newStartTime, selectedEndTime);
            if (duration < minHours) {
                setSelectedEndTime('');
            }
        }
        
        // Clear dateTime error when user selects time
        if (errors.dateTime) {
            setErrors(prev => ({ ...prev, dateTime: '' }));
        }
    };

    const handleEndTimeChange = (e) => {
        const newEndTime = e.target.value;
        setSelectedEndTime(newEndTime);
        
        // Clear dateTime error when user selects time
        if (errors.dateTime) {
            setErrors(prev => ({ ...prev, dateTime: '' }));
        }
    };

    const handleDatePickerContinue = () => {
        setShowDatePicker(false);
    };

    const toggleDatePicker = () => {
        if (availabilityUnavailable) return;
        setShowDatePicker(!showDatePicker);
    };

    // UseEffect hooks
    // Update available times when date changes
    useEffect(() => {
        if (!bookingData.date) {
            setTimes([]);
            setSelectedStartTime('');
            setSelectedEndTime('');
            return;
        }

        const availableSlots = generateTimeSlots();
        setTimes(availableSlots);

        // If current start time is not available in new slots, reset it
        if (selectedStartTime && !availableSlots.includes(selectedStartTime)) {
            setSelectedStartTime('');
            setSelectedEndTime('');
        } else if (selectedStartTime) {
            // If start time is valid, update end time options
            const endSlots = generateEndTimeSlots(selectedStartTime);
            
            // If current end time is not available in new slots, reset it
            if (selectedEndTime && !endSlots.includes(selectedEndTime)) {
                setSelectedEndTime('');
            }
        }
    }, [
        bookingData.date,
        bookingStudioData,
        generateEndTimeSlots,
        generateTimeSlots,
        selectedEndTime,
        selectedStartTime,
    ]);  // Watch bookingData.date instead

    // Remove the automatic date initialization useEffect - let user choose manually

    // Handle clicks outside of date picker
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if the click was on the date container
            const dateContainer = event.target.closest(`.${classes.dateContainer}`);
            if (dateContainer) {
                return; // Don't close if clicking the date container
            }
            
            if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
                setShowDatePicker(false);
            }
        };

        if (showDatePicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDatePicker]);

    return (
        <div className={classes.messageSenderContainer}>
            <header className={classes.header}>
                <div className={classes.headerContainer}>
                    <div className={classes.logoContainer} >
                        <button 
                            onClick={() => navigate('/')}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                        <svg xmlns="http://www.w3.org/2000/svg" width="63" height="22" viewBox="0 0 63 22" fill="none">
                            <path d="M59.881 10.9998C61.5389 10.9998 62.8828 9.65582 62.8828 7.99799C62.8828 6.34015 61.5389 4.99621 59.881 4.99621C58.2232 4.99621 56.8792 6.34015 56.8792 7.99799C56.8792 9.65582 58.2232 10.9998 59.881 10.9998Z" fill="#DF315A"/>
                            <path d="M11.1762 11.3413L9.54985 11.2047C5.52557 10.879 4.17024 10.4587 4.17024 9.54462C4.17024 8.63052 5.88004 8.12619 9.40389 8.12619C13.1258 8.12619 14.6271 8.62001 14.9399 9.94389L14.992 10.154H19.0788L19.0371 9.85983C18.6305 6.37153 15.5237 4.67992 9.54985 4.67992C3.57598 4.67992 0.510854 6.34001 0.510854 9.59716C0.510854 12.5811 2.96087 14.0206 8.98687 14.5669L10.4464 14.7035C14.4395 15.0503 15.9199 15.5756 15.9199 16.6263C15.9199 17.7715 14.0954 18.3599 10.4882 18.3599C6.40132 18.3599 4.3162 17.5929 4.11811 16.0064L4.08683 15.7752H0V16.0589C0.187661 19.8729 3.60726 21.8062 10.1754 21.8062C16.7435 21.8062 19.5897 19.999 19.5897 16.4372C19.5897 13.2641 17.2335 11.8351 11.1762 11.3413Z" fill="white"/>
                            <path d="M49.8447 5.20516V12.0136C49.8447 15.8277 47.3529 18.1917 43.3495 18.1917C39.5859 18.1917 38.1159 16.5001 38.1159 12.1502V5.20516H25.48V0.193359L21.7164 1.79041V16.1429C21.7164 20.104 23.6138 21.7956 28.0656 21.7956C29.619 21.7956 31.1098 21.5329 32.3713 21.2703L32.5799 21.2282V17.8555L32.2671 17.9185C30.6303 18.2548 29.9005 18.3808 28.7328 18.3808C26.5747 18.3808 25.48 17.866 25.48 15.0396V8.35724H36.2184L34.2375 9.87024V13.3795C34.2375 19.1268 36.6563 21.7956 41.8378 21.7956C45.1844 21.7956 47.9368 20.6293 49.8447 18.4019V21.2703H53.723V5.20516H49.8447Z" fill="white"/>
                        </svg>
                        </button>
                        <div className={classes.navSpans}>
                            <span className='regularMiddle'>Studio Seite</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="7" height="12" viewBox="0 0 7 12" fill="none">
                                <line x1="1.07622" y1="11.0713" x2="6.40434" y2="5.74323" stroke="#A1A7AE" strokeWidth="1.2"/>
                                <line x1="6.38724" y1="6.57445" x2="1.05913" y2="0.802326" stroke="#A1A7AE" strokeWidth="1.2"/>
                            </svg>
                            <span className='regularMiddle'>Nachricht schreiben</span>
                        </div>

                    </div>
                </div>
                <div className={`${classes.loginRegisterButtons}`} style={{gap: '20px'}}>
                    <button 
                        onClick={() => {
                            setActiveView('creator');
                            navigate('/creator');
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                        <div className={classes.switchProfile}>
                        <div className={classes.creatorViewContainer}>
                            <span className="slimRegularLargeText">Entdecker</span>
                        </div>
                        <span className={`slimRegularLargeText ${classes.hoverState}`}>Anbieter</span>
                        </div>
                    </button>
                    <div 
                        ref={userContainerRef} 
                        onClick={handleProfileMenuClick} 
                        className={classes.userContainer}
                    >
                        <div className={classes.userImageContainer}>
                        <img src={profilePicture} alt="User Profile" /> 
                        </div>
                    </div>
                </div>
            </header>

            <div className={classes.mainContentContainer}>
                <div className={classes.messageContainer}>
                    <div className={classes.messagesHeader}>
                        <div className={classes.userProfileContainer}>
                            <img src={studioUserProfilePicture || noUserImage} alt="Studio User Profile" />
                        </div>
                        <h2 className='titleSemibold2'>Erkundige dich bei {studioUserFirstName}</h2>
                    </div>

                    <div className={classes.userMainContainerPhone}>
                        <div className={classes.userMainInsideContainer}>
                            <div className={classes.studioPhotoContainer}>
                                <img src={studioImage} alt="Studio Image" />
                            </div>
                            <div className={classes.studioInfosContainer}>
                                <p className='mediumLarge'>{studioName}</p>
                                <span className={`${classes.city} slimRegularSmallText`}>{city}</span>
                                {rating !== null && (                            
                                    <div className={classes.ratingContainer}>
                                        <span className='slimRegularSmallText'>{rating}</span>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                            <g clipPath="url(#clip0_13684_33557)">
                                                <path d="M6.69917 5.54951L7.01998 7.42977L4.60059 6.16438L6.56105 4.75195L6.65462 5.29108L6.69917 5.54951Z" fill="#1D1E1B"/>
                                                <path d="M2.54896 5.29081L2.64253 4.75168L0.686523 2.85805L3.39107 2.46596L4.60299 0.0332031L5.81492 2.46596L8.51947 2.85805L2.1836 7.4295L2.54896 5.29081Z" fill="#1D1E1B"/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_13684_33557">
                                                    <rect width="7.83294" height="7.39629" fill="white" transform="translate(0.686523 0.0332031)"/>
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    </div>
                                )}
                            </div>
                        </div>
                        {selectedStartTime && selectedEndTime && calculateDuration(selectedStartTime, selectedEndTime) !== 0 && (() => {
                            const duration = calculateDuration(selectedStartTime, selectedEndTime);
                            const basePrice = studioPricePerHour * duration;
                            const total = Math.round(basePrice * 100) / 100;

                            return (
                                <div className={classes.priceContainer}>
                                    <h3 className='titleSemibold3'>Preis (unverbindlich)</h3>
                                    <div className={classes.priceDetails}>
                                        <p className='regularLarge'>{studioPricePerHour} € x {duration} Stunden</p>
                                        <p className='regularLarge'>{basePrice} €</p>
                                    </div>
                                    <div className={classes.totalContainer}>
                                        <p className='titleSemibold3'>{total} €</p>
                                        <p className='titleSemibold3'>Gesamtbetrag</p>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>

                    <div className={classes.infosContainer}>
                        <span className={classes.infosSpan}>
                            💡 Das ist nur eine Erkundigung – keine Kosten, keine Verpflichtung.
                        </span>
                    </div>
                    <div className={classes.dateTimeContainer}>
                        <div className={classes.dateTimeHeader}>
                            <h4 className='semiboldMiddle'>Datum & Uhrzeit</h4>
                            <div className={classes.switchContainer}>
                                <label className={classes.switch}>
                                    <input 
                                        type="checkbox" 
                                        checked={isChecked}
                                        onChange={(e) => {
                                            setIsChecked(e?.target?.checked);
                                            setSelectedStartTime('');
                                            setSelectedEndTime('');
                                            // Clear both date/time and message errors when toggling
                                            setErrors(prev => ({ ...prev, dateTime: '', message: '' }));
                                        }}
                                    />
                                    <span className={classes.slider}></span>
                                </label>
                            </div>
                        </div>
                        {isChecked && (
                        <div className={classes.dateTimeDetails}>
                            <div className={classes.dateInput}>
                                <div 
                                    className={classes.dateContainer}
                                    onClick={toggleDatePicker}
                                    >
                                    {bookingData.date ? format(parseLocalDateString(bookingData.date), 'dd.MM.yyyy', { locale: de }) : 'Datum wählen'}
                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="8" viewBox="0 0 15 8" fill="none">
                                        <path d="M1 1.01579L7.59865 7M6.85146 6.98421L14 1" stroke="#1F1F1F"/>
                                    </svg >
                                </div>
                                {showDatePicker &&
                                    <div className={classes.datePickerOverlay} ref={datePickerRef}>
                                    <DateChooser 
                                        fromDesktop={true}
                                        nextSlide={handleDatePickerContinue} 
                                        bookingStudioData={bookingStudioData}
                                    />
                                    </div>
                                }
                            </div>
                            <div className={`${classes.timeOptionContainer} regularLarge`}>
                                <div className={classes.labelContainer}>
                                    <select 
                                        className={`${classes.select} regularLarge ${classes.smaller}`} 
                                        id="start" 
                                        value={selectedStartTime} 
                                        onChange={handleStartTimeChange}
                                        disabled={availabilityUnavailable}
                                        >
                                        <option value="">Start</option>
                                        {times.map((time, index) => (
                                            <option key={index} value={time}>{time}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className={classes.verticalLine}></div>
                                <div className={classes.labelContainer}>
                                    <select 
                                        className={`${classes.select} regularLarge ${classes.smaller}`} 
                                        id="end" 
                                        value={selectedEndTime} 
                                        onChange={handleEndTimeChange} 
                                        disabled={!selectedStartTime || availabilityUnavailable}
                                    >
                                        <option value="">Ende</option>
                                        {selectedStartTime && generateEndTimeSlots(selectedStartTime).map((time, index) => (
                                            <option key={index} value={time}>{time}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            {selectedStartTime && selectedEndTime && calculateDuration(selectedStartTime, selectedEndTime) !== 0 &&
                            <div className={classes.textContainer}>
                                <p className="regularXxs">Dauer: {calculateDuration(selectedStartTime, selectedEndTime)} Stunden</p>
                            </div>
                            }
                            {availabilityUnavailable && (
                                <div className={classes.errorMessage}>
                                    <span className='slimRegularSmallText'>
                                        Verfügbarkeit konnte gerade nicht geladen werden. Bitte versuche es später erneut.
                                    </span>
                                </div>
                            )}
                            <div 
                                onClick={(e) => {
                                    if (e.target.type !== 'checkbox') {
                                        handleFlexibleChange({ target: { checked: !isFlexible } });
                                    }
                                }}
                                className={classes.checkboxContainer}
                            >
                                <input 
                                    className={classes.checkboxInput} 
                                    type="checkbox" 
                                    checked={isFlexible}
                                    onChange={handleFlexibleChange}
                                />
                                <span className="regularMiddle">Ich bin zeitlich flexibel</span>
                            </div>
                        </div>
                        )}
                        {errors?.dateTime && (
                            <div className={classes.errorMessage}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="12" viewBox="0 0 11 12" fill="none">
                                    <path d="M0.527344 0.707031L10.4725 10.6522" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10"/>
                                    <path fillRule="evenodd" clipRule="evenodd" d="M4.4452 5.67969L0 1.23449L1.0548 0.179688L5.5 4.62488L9.9452 0.179688L11 1.23449L6.5548 5.67969L11 10.1249L9.9452 11.1797L5.5 6.73449L1.0548 11.1797L0 10.1249L4.4452 5.67969Z" fill="#CB1541"/>
                                </svg>
                                <span className='slimRegularSmallText'>{errors?.dateTime}</span>
                            </div>
                        )}
                    </div>
                    <div className={classes.messageInputContainer}>
                        <h3 className='semiboldMiddle'>Nachricht an den Host</h3>
                        <textarea 
                            className={`${classes.messageInput} slimRegularLargeText`} 
                            placeholder='Projektbeschreibung'
                            value={messageText}
                            onChange={handleMessageChange}
                        />
                        {errors?.message && (
                            <div className={classes.errorMessage}>  
                                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="12" viewBox="0 0 11 12" fill="none">
                                    <path d="M0.527344 0.707031L10.4725 10.6522" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10"/>
                                    <path fillRule="evenodd" clipRule="evenodd" d="M4.4452 5.67969L0 1.23449L1.0548 0.179688L5.5 4.62488L9.9452 0.179688L11 1.23449L6.5548 5.67969L11 10.1249L9.9452 11.1797L5.5 6.73449L1.0548 11.1797L0 10.1249L4.4452 5.67969Z" fill="#CB1541"/>
                                </svg>
                                <span className='slimRegularSmallText'>{errors?.message}</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className={classes.userMainContainer}>
                    <div className={classes.userMainInsideContainer}>
                        <div className={classes.studioPhotoContainer}>
                            <img src={studioImage} alt="Studio Image" />
                        </div>
                        <div className={classes.studioInfosContainer}>
                            <p className='mediumLarge'>{studioName}</p>
                            <span className={`${classes.city} slimRegularSmallText`}>{city}</span>
                            {rating !== null && (                            
                                <div className={classes.ratingContainer}>
                                    <span className='slimRegularSmallText'>{rating}</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                                        <g clipPath="url(#clip0_13684_33557)">
                                            <path d="M6.69917 5.54951L7.01998 7.42977L4.60059 6.16438L6.56105 4.75195L6.65462 5.29108L6.69917 5.54951Z" fill="#1D1E1B"/>
                                            <path d="M2.54896 5.29081L2.64253 4.75168L0.686523 2.85805L3.39107 2.46596L4.60299 0.0332031L5.81492 2.46596L8.51947 2.85805L2.1836 7.4295L2.54896 5.29081Z" fill="#1D1E1B"/>
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_13684_33557">
                                                <rect width="7.83294" height="7.39629" fill="white" transform="translate(0.686523 0.0332031)"/>
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </div>
                            )}
                        </div>
                    </div>
                    {selectedStartTime && selectedEndTime && calculateDuration(selectedStartTime, selectedEndTime) !== 0 && (() => {
                        const duration = calculateDuration(selectedStartTime, selectedEndTime);
                        const basePrice = studioPricePerHour * duration;
                        const total = Math.round(basePrice * 100) / 100;

                        return (
                            <div className={classes.priceContainer}>
                                <h3 className='titleSemibold3'>Preis (unverbindlich)</h3>
                                <div className={classes.priceDetails}>
                                    <p className='regularLarge'>{studioPricePerHour} € x {duration} Stunden</p>
                                    <p className='regularLarge'>{basePrice} €</p>
                                </div>
                                <div className={classes.totalContainer}>
                                    <p className='titleSemibold3'>{total} €</p>
                                    <p className='titleSemibold3'>Gesamtbetrag</p>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>
            <footer className={classes.footerContainer}>
                <button className={classes.backButton} onClick={() => window.history.back()}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="19" viewBox="0 0 11 19" fill="none">
                        <line x1="10.0996" y1="0.671353" x2="1.10103" y2="9.6699" stroke="#1F1F1F" strokeWidth="1.7"/>
                        <line x1="1.12556" y1="8.49182" x2="10.1241" y2="18.2402" stroke="#1F1F1F" strokeWidth="1.7"/>
                    </svg>
                    <span className='mediumLarge'>Zurück</span>
                </button>
                {isFormValid ? (
                    <button 
                        className={`${classes.sendButton} mediumLarge`}
                        onClick={handleSendMessage}
                        disabled={isSendingMessage || creatingChatRoom}
                        style={{ backgroundColor: 'var(--dark-grey)', cursor: (isSendingMessage || creatingChatRoom) ? 'wait' : 'pointer' }}
                    >
                        <span className={`${classes.contactButton} semiboldXs`}>
                            {(isSendingMessage || creatingChatRoom) ? 'Wird gesendet...' : 'Nachricht schicken'}
                        </span>
                    </button>
                ) : (
                    <button 
                        className={`${classes.sendButton} mediumLarge`}
                        onClick={handleSendMessage}
                        style={{ backgroundColor: 'var(--grey-1)', cursor: 'default' }}
                    >
                        <span 
                            className={`${classes.contactButton} semiboldXs`} 
                            style={{ cursor: 'default' }}
                        >
                            Nachricht schicken
                        </span>
                    </button>
                )}
            </footer>
            {showProfileMenu && (
                < ProfileMenu
                    ref={profileMenuRef}
                    setShowProfileMenu={setShowProfileMenu}
                />
            )}
            {showChatSelectionModal && (
                <SmallModal 
                    open={showChatSelectionModal}
                    onBackdropClick={() => setShowChatSelectionModal(false)}
                >
                    <div className={classes.chatSelectionModal}>
                        <header className={classes.chatSelectionHeader}>
                            <h3 className='titleSemibold2'>Chat auswählen</h3>
                            <button 
                                onClick={() => setShowChatSelectionModal(false)}
                                className={classes.closeButton}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="12" viewBox="0 0 11 12" fill="none">
                                    <path d="M0.527344 0.707031L10.4725 10.6522" stroke="#1F1F1F" strokeWidth="1.5" strokeMiterlimit="10"/>
                                    <path fillRule="evenodd" clipRule="evenodd" d="M4.4452 5.67969L0 1.23449L1.0548 0.179688L5.5 4.62488L9.9452 0.179688L11 1.23449L6.5548 5.67969L11 10.1249L9.9452 11.1797L5.5 6.73449L1.0548 11.1797L0 10.1249L4.4452 5.67969Z" fill="#1F1F1F"/>
                                </svg>
                            </button>
                        </header>
                        <div className={classes.chatSelectionContent}>
                            <p className='regularMiddle' style={{ marginBottom: '20px' }}>
                                Du hast bereits mehrere Chats mit {studioUserFirstName}. Wähle einen aus:
                            </p>
                            <div className={classes.chatList}>
                                {existingChatsForStudio.map((chat) => (
                                    <button
                                        key={chat.id}
                                        onClick={() => handleChatRoomSelect(chat.id)}
                                        className={classes.chatOption}
                                    >
                                        <div className={classes.chatOptionInfo}>
                                            <p className='regularLarge'>
                                                {chat.chatType === 'INQUIRY' ? 'Erkundigung' : 'Buchung'}
                                            </p>
                                            {chat.lastMessageAt && (
                                                <span className='slimRegularSmallText'>
                                                    {format(new Date(chat.lastMessageAt), 'dd.MM.yyyy', { locale: de })}
                                                </span>
                                            )}
                                        </div>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="7" height="12" viewBox="0 0 7 12" fill="none">
                                            <line x1="1.07622" y1="11.0713" x2="6.40434" y2="5.74323" stroke="#A1A7AE" strokeWidth="1.2"/>
                                            <line x1="6.38724" y1="6.57445" x2="1.05913" y2="0.802326" stroke="#A1A7AE" strokeWidth="1.2"/>
                                        </svg>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </SmallModal>
            )}
        </div>
    );
};

export default MessageSender;
